import { defineMessages, useIntl } from 'react-intl';

import { formatGracePeriodDuration } from '../../utils/gracePeriodFormat';
import { GracePeriodNotice } from '../GracePeriodNotice';

export type ProtectedRoleWarningContext = 'invite' | 'edit';

export type ProtectedRoleWarningProps = {
  /** Display name of the protected role, e.g. "Admin". */
  roleName: string;
  /** Grace period of the role (see `getRoleProtection`), e.g. 48h. */
  gracePeriodMs: number;
  /**
   * `invite` or `edit`. Both show the same body today; they have separate
   * messages so the copy can differ per path.
   */
  context: ProtectedRoleWarningContext;
  testID?: string;
};

const messages = defineMessages({
  title: {
    id: 'Team.protectedRoleWarning.title',
    defaultMessage: 'Newly granted {roleName} access will start after a delay of {duration}.',
    description:
      'Warning title in the invite and edit member trays when a protected role is selected. {roleName} is the role name, e.g. "Admin". {duration} is the delay length, e.g. "48 hours".',
  },
  inviteBody: {
    id: 'Team.protectedRoleWarning.inviteBody',
    defaultMessage: 'Viewer access applies until then. All admins will be notified.',
    description:
      'Warning body in the invite tray when a protected role (e.g. Admin) is selected. The invitee has view-only access until the role starts, and every workspace admin gets an email about the invite.',
  },
  editBody: {
    id: 'Team.protectedRoleWarning.editBody',
    defaultMessage: 'Viewer access applies until then. All admins will be notified.',
    description:
      'Warning body in the edit member tray when a protected role (e.g. Admin) is newly selected. The member has view-only access until the new role starts, and every workspace admin gets an email about the change.',
  },
});

const BODY_MESSAGES = {
  invite: messages.inviteBody,
  edit: messages.editBody,
} as const;

/**
 * Summary warning shown once below the access group of the invite or edit tray
 * when a protected role (e.g. Admin) is selected. One notice covers every
 * selected protected role and entity.
 *
 * Shows the full grace period because the clock starts when the invite or role
 * change is submitted, not when the warning is shown.
 */
export function ProtectedRoleWarning({
  roleName,
  gracePeriodMs,
  context,
  testID = 'protected-role-warning',
}: ProtectedRoleWarningProps) {
  const intl = useIntl();

  return (
    <GracePeriodNotice
      title={intl.formatMessage(messages.title, {
        roleName,
        duration: formatGracePeriodDuration(intl, gracePeriodMs),
      })}
      body={intl.formatMessage(BODY_MESSAGES[context])}
      testID={testID}
    />
  );
}

export type NewlyAddedProtectedRole = { legalEntityId: string; role: string };

/**
 * Protected roles in `selectedByEntity` that the member doesn't already have on
 * the same legal entity. Both maps are keyed by legal entity ID, because the
 * same role is granted per entity. `currentByEntity` is the member's effective
 * roles per entity, so re-saving a held role doesn't warn again. A missing key
 * means the member has no roles on that entity, which covers the invite case.
 * The edit tray shows the warning when the result is non-empty.
 */
export function getNewlyAddedProtectedRoles(
  selectedByEntity: Record<string, readonly string[]>,
  currentByEntity: Record<string, readonly string[]>,
  isProtectedRole: (role: string) => boolean,
): NewlyAddedProtectedRole[] {
  return Object.entries(selectedByEntity).flatMap(([legalEntityId, roles]) => {
    const currentRoles = currentByEntity[legalEntityId] ?? [];
    return roles
      .filter((role) => isProtectedRole(role) && !currentRoles.includes(role))
      .map((role) => ({ legalEntityId, role }));
  });
}
